package club.srams.website.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun CarouselOfSections(
    items: List<@Composable () -> Unit>
    , frameDuration: Duration
    , fadeDurationMs: Int = 1000
    , modifier: Modifier = Modifier
) {
    var currentIndex by remember { mutableStateOf(0) }

    LaunchedEffect(Unit) {
        while (true) {
            delay(frameDuration)
            currentIndex = (currentIndex + 1) % items.size
        }
    }

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            .borderRadius(12.px)
    ) {
        // ── Ghost item ────────────────────────────────────────────────────────
        // Stays in normal flow so the parent Box gets the right height.
        // Invisible and non-interactive; the real slides are layered on top.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("visibility", "hidden")
                    property("pointer-events", "none")
                }
        ) {
            items[0]()
        }

        // ── Fade layers ───────────────────────────────────────────────────────
        // All slides rendered and absolutely positioned; only the active one
        // has opacity 1 — the CSS transition does the crossfade.
        items.forEachIndexed { i, item ->
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px)
                    .left(0.px)
                    .width(100.percent)
                    .height(100.percent)
                    .opacity(if (i == currentIndex) 1.0 else 0.0)
                    .styleModifier {
                        property("transition", "opacity ${fadeDurationMs}ms ease-in-out")
                    }
            ) {
                item()
            }
        }

        // Left arrow
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .position(Position.Absolute)
                .left(12.px)
                .size(40.px)
                .borderRadius(50.percent)
                .backgroundColor(rgba(0, 0, 0, 0.5))
                .cursor(Cursor.Pointer)
                .zIndex(1)
                .onClick { currentIndex = (currentIndex - 1 + items.size) % items.size }
        ) {
            SpanText("‹", modifier = Modifier.color(Colors.White).fontSize(24.px))
        }

        // Right arrow
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .position(Position.Absolute)
                .right(12.px)
                .size(40.px)
                .borderRadius(50.percent)
                .backgroundColor(rgba(0, 0, 0, 0.5))
                .cursor(Cursor.Pointer)
                .zIndex(1)
                .onClick { currentIndex = (currentIndex + 1) % items.size }
        ) {
            SpanText("›", modifier = Modifier.color(Colors.White).fontSize(24.px))
        }

        // Dot indicators
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.px),
            modifier = Modifier
                .position(Position.Absolute)
                .bottom(12.px)
                .zIndex(1)
        ) {
            items.indices.forEach { i ->
                Box(
                    modifier = Modifier
                        .size(10.px)
                        .borderRadius(50.percent)
                        .backgroundColor(
                            if (i == currentIndex) Colors.White
                            else rgba(255, 255, 255, 0.5)
                        )
                        .cursor(Cursor.Pointer)
                        .onClick { currentIndex = i }
                )
            }
        }
    }
}